import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../common/types';
import { SmartLinkFilter } from './types';
import styles from './SMLFilterTabs.module.scss';

interface SMLFilterTabsProps {
  value: SmartLinkFilter;
  onChange: (value: SmartLinkFilter) => void;
  /** Shown as "{shown} of {total}". */
  shown: number;
  total: number;
}

const SMLFilterTabsComponent: React.FC<SMLFilterTabsProps> = ({ value, onChange, shown, total }) => {
  const intl = useIntl();
  const selectAll = useCallback(() => onChange('all'), [onChange]);
  const selectLive = useCallback(() => onChange('live'), [onChange]);
  const selectDraft = useCallback(() => onChange('draft'), [onChange]);

  // Scheduled is in the design but nothing can hold that state yet, so the tab
  // would always be empty.
  const tabs: { key: SmartLinkFilter; label: string; onClick: () => void }[] = [
    {
      key: 'all',
      label: intl.formatMessage({ id: 'app.sml.smartlinks.filter.all', defaultMessage: 'All' }),
      onClick: selectAll,
    },
    {
      key: 'live',
      label: intl.formatMessage({ id: 'app.sml.smartlinks.filter.live', defaultMessage: 'Live' }),
      onClick: selectLive,
    },
    {
      key: 'draft',
      label: intl.formatMessage({ id: 'app.sml.smartlinks.filter.drafts', defaultMessage: 'Drafts' }),
      onClick: selectDraft,
    },
  ];

  return (
    <div className={styles.toolbar}>
      <div className={styles.seg}>
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={clsx(styles.tab, value === tab.key && styles.tabOn)}
            onClick={tab.onClick}
            aria-pressed={value === tab.key}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <span className={styles.count}>
        {intl.formatMessage(
          { id: 'app.sml.smartlinks.filter.count', defaultMessage: '{shown} of {total}' },
          { shown, total }
        )}
      </span>
    </div>
  );
};

export const SMLFilterTabs = TypedMemo(SMLFilterTabsComponent);
